import { usePublicationsQuery } from "@/shared/services/useTheme";

export type HasEverPublished = {
  /** At least one row in `store_theme_publications` for this store. */
  hasEverPublished: boolean;
  /**
   * The publications list has answered. Until it has, `hasEverPublished` is
   * `false` because it is built from absent data — the same gate
   * `useShareReadiness` and `useDashboardSetupGuide` put in front of their own
   * derived facts, and for the same reason: a store that HAS published must
   * never flash the never-published state on a cold open.
   */
  answered: boolean;
};

/**
 * Has this store's storefront ever been published?
 *
 * One definition, two readers: the dashboard setup guide's «انشر متجرك
 * للزباين» row and the editor's first-publish moment. They are answers to the
 * same question, and a question answered twice is a question that eventually
 * gets two answers — the reason `resolveHasPaymentWay` was pulled into
 * `shared/lib/store-readiness.ts`. Moved here verbatim from
 * `useDashboardSetupGuide`; the guide's unchanged test is the evidence the
 * move changed nothing.
 *
 * ⚠️ THIS SIGNAL IS POISONED IN ONE DIRECTION, and callers must respect it
 * (B84 in `docs/backlog.md`). A brand write — the wizard's logo step, any
 * Store Settings → Branding colour change — inserts a publication row too, so
 * `true` does NOT prove the merchant ever published a DESIGN. It is therefore
 * safe to use for SUPPRESSING first-time language (we then say less, never
 * something false) and unsafe for claiming a publish happened. The editor uses
 * it in the safe direction only.
 *
 * Costs one metadata-only request on the same cache key the version history
 * already uses (30s staleTime); payloads stay in the database.
 */
export function useHasEverPublished(enabled: boolean): HasEverPublished {
  const publications = usePublicationsQuery(enabled);
  return {
    hasEverPublished: (publications.data?.items.length ?? 0) > 0,
    answered: publications.data !== undefined,
  };
}
